// types/promotion.ts

export interface LocalizedText {
  title: string
  bannerTag: string
  productTag: string
  description: string
  excerpt: string
}

export enum ConditionType {
  BUY = 'BUY',
}

export enum ItemType {
  ANY = 'ANY',
  PRODUCT_ID = 'PRODUCT_ID',
  PRODUCT_LINE = 'PRODUCT_LINE',
  PRODUCT_CONTAINER = 'PRODUCT_CONTAINER',
  PRODUCT_GROUP = 'PRODUCT_GROUP',
  PRODUCT_SIZE = 'PRODUCT_SIZE',
  CATEGORY = 'CATEGORY',
  SUBCATEGORY = 'SUBCATEGORY',
  BRAND = 'BRAND',
  SUBBRAND = 'SUBBRAND',
  GTIN = 'GTIN',
}

export enum LogicalOperator {
  AND = 'AND',
  OR = 'OR',
}

export type AnyOptionType = 'ANY'

export interface ExcludedItem {
  id: string
  displayText: string
}

export interface SelectedItem {
  id: string
  displayText: string
}

export interface Condition {
  type: ConditionType
  // We need to use string type for quantity to accomodate 'Any' value
  quantity?: number | null
  amount?: number | null
  layeredQuantity?: number[] | null
  layeredAmount?: number[] | null
  itemType: ItemType
  items: Item[]
  productCombination?: boolean // Optional, default false
}

export interface ConditionGroup {
  combinedWith?: LogicalOperator // Optional, default is AND
  conditions: Condition[]
}

export interface ConditionGroup {
  conditions: Condition[]
}

export interface PromotionCondition {
  sequence: number
  conditionGroup?: ConditionGroup
  rewards?: Reward[]
}

export enum SegmentType {
  ALL_CUSTOMERS = 'ALL_CUSTOMERS',
  SELECTED_GROUPS = 'SELECTED_GROUPS',
  SELECTED_SEGMENTS = 'SELECTED_SEGMENTS',
}

export type SegmentSelectionType = 'SEGMENT_LIST' | 'SEGMENT_FOLDER'

/**
 * Details of a segment
 */
export interface SegmentDetails {
  segmentId?: string
  segmentName?: string
  segmentFolderId?: string
}

/**
 * Configuration for customer segments
 */
export interface SegmentConfiguration {
  selectionType?: SegmentSelectionType
  segments: SegmentDetails[]
}

/**
 * Segment configuration wrapper for the promotion
 * If allCustomers is true, then includedSegments is not required
 * Mandatory if allCustomers is false
 */
export interface SegmentConfigurationWrapper {
  includedSegments?: SegmentConfiguration[]
  excludedSegment?: SegmentConfiguration
}

export enum RewardType {
  FREE_PRODUCT = 'FREE_PRODUCT',
  POINTS = 'POINTS',
  DISCOUNT = 'DISCOUNT',
  GIFT_ITEM = 'GIFT_ITEM',
  FREE_PRODUCT_FROM_LIST = 'FREE_PRODUCT_FROM_LIST',
  PERCENTAGE_DISCOUNT = 'PERCENTAGE_DISCOUNT',
  CASH_DISCOUNT = 'CASH_DISCOUNT',
}

export type DiscountType = 'PERCENTAGE' | 'AMOUNT' | 'FIXED_AMOUNT'

export type DiscountAppliedOn = 'GROSS' | 'MRP'

export type GiftItemType = 'VOUCHER'

export interface FreeProductReward {
  type: RewardType.FREE_PRODUCT
  quantity?: number
  layeredQuantity?: number[]
  items: Item[]
  itemType: ItemType
}

export interface FreeProductRewardFromList {
  type: RewardType.FREE_PRODUCT_FROM_LIST
  lists: FreeProductListDetails[]
}

export interface FreeProductListDetails {
  quantity: number
  items: string[]
  itemType: ItemType
  sequence: number
}

export interface PointsReward {
  type: RewardType.POINTS
  points?: number
  layeredPoints?: number[]
}

export interface DiscountReward {
  type: RewardType.DISCOUNT
  discountType: DiscountType
  discountValue?: number
  layeredDiscountValue?: number[]
  discountAmountUpto?: number | null
  layeredDiscountAmountUpto?: number[] | null
  discountAppliedOn?: DiscountAppliedOn
}

export interface GiftItemReward {
  type: RewardType.GIFT_ITEM
  quantity?: number | null
  layeredQuantity?: number[] | null
  items: GiftItemType[]
}

/**
 * Union type that resolves based on the `type` discriminator.
 */
export type Reward = FreeProductReward | PointsReward | DiscountReward | GiftItemReward

export interface PromotionConditionWrapper {
  reward?: Reward
}

export enum PromotionType {
  BUY_SINGLE_PRODUCT = 'BUY_SINGLE_PRODUCT',
  BUY_MULTIPLE_PRODUCTS = 'BUY_MULTIPLE_PRODUCTS',
  STORE_DISCOUNT = 'STORE_DISCOUNT',
  WELCOME_DISCOUNT = 'WELCOME_DISCOUNT',
}
export enum NewDiscountType {
  WELCOME_DISCOUNT = 'WELCOME_PERCENTAGE',
  STORE_DISCOUNT = 'STORE_PERCENTAGE',
}
export enum ApiToUiDiscountType {
  WELCOME_PERCENTAGE = 'WELCOME_DISCOUNT',
  STORE_PERCENTAGE = 'STORE_DISCOUNT',
}
export enum DisplayPage {
  HOME_PAGE = 'HOME_PAGE',
  PRODUCT_PAGE = 'PRODUCT_PAGE',
  PRODUCT_CARD = 'PRODUCT_CARD',
  PRODUCT_CATALOGUE = 'PRODUCT_CATALOGUE',
}

export enum PromotionStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export interface PromotionText {
  [locale: string]: LocalizedText
}

export interface SelectedSegmentGroup {
  groupId: string
  groupName: string
  segments: Array<{
    segmentId: string
    segmentName: string
  }>
}

export interface Item {
  id: string
  name: string
  uom: string
}

export interface ExcludedItemsType {
  /** The itemType refers to the ItemType selected in the UI (e.g. PRODUCT_ID) */
  itemType: string
  /** List of item identifiers being excluded */
  items: Item[]
}

export interface Promotion {
  id?: string
  name: string
  promotionType: PromotionType
  excludedItems: ExcludedItemsType[]

  promotionConditions: PromotionCondition[]
  promotionCondition?: PromotionConditionWrapper
  segmentConfiguration: SegmentConfigurationWrapper
  text: PromotionText
  bannerUrl: string
  displayOn: DisplayPage[]
  displayOrder: number
  activeStartDate: string // ISO 8601 date string
  activeEndDate: string // ISO 8601 date string
  active: boolean
  maxAllowedOrderPerStore: number
  maxFreeItemsPerOrder?: number
  maxTimesPerOrder?: number
  maxAllowedPercentage?: number
  resetFrequencyInDays: number
  bannerType?: string
  landingPage?: string
  behavior: string
  status: PromotionStatus
  routeTypeId?: string
  allCustomers: boolean
  source: string
}

export enum PromotionStep {
  SETUP = 1,
  DISPLAY = 2,
  ATTRIBUTES = 3,
  REVIEW = 4,
}
export const rewardTypeToIdMap: Record<string, Record<string, string>> = {
  DISCOUNT: {
    PERCENTAGE: 'PERCENTAGE_DISCOUNT',
    AMOUNT: 'CASH_DISCOUNT',
  },
  FREE_PRODUCT: {
    FREE_PRODUCT: 'FREE_PRODUCT',
    FREE_PRODUCT_FROM_LIST: 'FREE_PRODUCT_FROM_LIST',
  },
  GIFT_ITEM: {
    GIFT_ITEM: 'GIFT_ITEM',
  },
  POINTS: {
    POINTS: 'POINTS',
  },
}
export enum RewardTypeMap {
  POINTS = 'points',
  PERCENTAGE = 'discountValue',
  DISCOUNT = 'discountValue',
}
export enum CurrencyType {
  IND = '₹',
  MX = '$',
}
